import { UniqueIdentifier, useDndContext } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ComponentType, useCallback, useMemo } from 'react';

import { useCrossListDnd } from './CrossListDndProvider';
import { DndKitSortableItem } from './DndKitSortableItem';
import { isDragItem } from './typeGuards';
import { DragItem, DropValidator } from './types';

type ObjectWithId = {
  id: UniqueIdentifier;
  sortable?: boolean;
  relatedTo?: UniqueIdentifier;
};

type DndKitSortableListProps<T extends ObjectWithId> = {
  items: T[];
  setItems: (items: T[]) => void;
  itemType: DragItem['itemType'];
  activeId?: string;
  disabled?: boolean;
  Component?: ComponentType<T>;
  canAcceptDrop?: DropValidator;
  onDrop?: DropValidator;
};

export function DndKitSortableList<T extends ObjectWithId>({
  items,
  setItems,
  itemType,
  activeId,
  disabled,
  Component,
  canAcceptDrop,
  onDrop,
}: DndKitSortableListProps<T>) {
  const dndContext = useDndContext();
  const crossListDndContext = useCrossListDnd();

  const getMappedItems = useCallback((initialItems: T[]) => {
    const groups: Map<UniqueIdentifier, T[]> = new Map();
    const processed = new Set<UniqueIdentifier>();

    initialItems.forEach((item) => {
      if (processed.has(item.id)) return;

      const relatedItems = initialItems.filter((other) => other.relatedTo === item.id);

      const group = [item, ...relatedItems];
      groups.set(item.id, group);

      group.forEach((i) => processed.add(i.id));
    });

    return Array.from(groups.entries()).map(([id, items]) => ({
      id,
      items,
    }));
  }, []);

  const mappedItems = useMemo(() => getMappedItems(items), [items, getMappedItems]);
  const sortableIds = useMemo(() => mappedItems.map((item) => item.id), [mappedItems]);

  const handleSort = useCallback(
    (activeId: UniqueIdentifier, overId: UniqueIdentifier) => {
      const currentMappedItems = getMappedItems(items);
      const activeIndex = currentMappedItems.findIndex((item) => item.id === activeId);
      const overIndex = currentMappedItems.findIndex((item) => item.id === overId);

      if (activeIndex !== -1 && overIndex !== -1) {
        const sortedItems = arrayMove(currentMappedItems, activeIndex, overIndex);
        const newState: T[] = sortedItems.flatMap((item) => item.items);

        setItems(newState);
      }
    },
    [items, getMappedItems, setItems]
  );

  if (!dndContext || !crossListDndContext) {
    throw new Error('DndKitSortableList must be wrapped in CrossListDndProvider');
  }

  return (
    <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
      <div className="flex flex-col gap-[1px] w-full bg-elevated-1 overflow-y-auto">
        {mappedItems.map((item) => {
          const firstItem = item.items[0];
          const activeData = dndContext.active?.data.current;
          const overData = dndContext.over?.data.current;

          const canAccept =
            isDragItem(activeData) &&
            isDragItem(overData) &&
            !!canAcceptDrop?.(activeData, overData);

          const isOver = dndContext.over?.id === item.id && canAccept;

          return (
            <DndKitSortableItem
              key={item.id}
              id={item.id}
              isDroppable={canAccept}
              isOver={isOver}
              disabled={disabled}
              itemType={itemType}
              onSort={handleSort}
              onDrop={onDrop}
              props={firstItem}
            >
              <div className="flex flex-col gap-1">
                {item.items.map((nestedItem) => {
                  const active = nestedItem.id === activeId;

                  if (Component) {
                    return (
                      <div
                        key={nestedItem.id}
                        className={active ? 'bg-elevated-2' : 'bg-elevated-1'}
                      >
                        <Component {...nestedItem} />
                      </div>
                    );
                  }

                  return null;
                })}
              </div>
            </DndKitSortableItem>
          );
        })}
      </div>
    </SortableContext>
  );
}

export default DndKitSortableList;
